import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, MapPin } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { API } from "../../lib/constants";

const ServiceAreasSection = () => {
  const [suburb, setSuburb] = useState("");
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const FREE_RADIUS_KM = 15;
  const FEE_PER_KM = 0.40;

  // Popular suburbs for quick selection grid
  const suburbs = [
    { name: "Thomastown", distance: 0 },
    { name: "Bundoora", distance: 5 },
    { name: "Mill Park", distance: 4 },
    { name: "Lalor", distance: 3 },
    { name: "Epping", distance: 5 },
    { name: "Preston", distance: 8 },
    { name: "Reservoir", distance: 6 },
    { name: "South Morang", distance: 7 },
    { name: "Doncaster", distance: 12 },
    { name: "Sunshine", distance: 18 },
    { name: "Footscray", distance: 20 },
    { name: "Melbourne CBD", distance: 18 }
  ];

  const calculateTravelFee = (distance) => {
    if (distance <= FREE_RADIUS_KM) return 0;
    return (distance - FREE_RADIUS_KM) * FEE_PER_KM;
  };

  // Fetch Google Places Autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setPredictions([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { query }
      });
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Autocomplete error:", error);
      setPredictions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSuburb(value);
    setShowSuggestions(true);
    setCalculatedFee(null);
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const selectPrediction = async (prediction) => {
    setSuburb(prediction.description || prediction.main_text);
    setShowSuggestions(false);
    setPredictions([]);
    await calculateFee(prediction.description || prediction.main_text);
  };

  const calculateFee = async (address) => {
    const searchAddress = address || suburb;
    if (!searchAddress.trim()) return;
    
    setLoading(true);
    setShowSuggestions(false);
    setPredictions([]);
    
    try {
      const response = await axios.post(`${API}/calculate-travel-fee`, {
        address: searchAddress
      });
      
      if (response.data.success) {
        setCalculatedFee({
          fee: response.data.fee,
          distance: response.data.distance_km,
          suburb: searchAddress,
          source: "google"
        });
      } else {
        setCalculatedFee({ 
          fee: calculateTravelFee(25), 
          distance: 25, 
          suburb: searchAddress, 
          unknown: true, 
          source: "estimate" 
        });
      }
    } catch (error) {
      console.error("Error calculating fee:", error);
      setCalculatedFee({ 
        fee: calculateTravelFee(25), 
        distance: 25, 
        suburb: searchAddress, 
        unknown: true, 
        source: "estimate" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="service-areas" className="section-spacing bg-slate-900/50" data-testid="service-areas-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Service <span className="gradient-text">Areas</span>
          </h2>
          <p className="text-slate-400 mb-8">
            Based in Thomastown, servicing Melbourne's North & West
          </p>
        </motion.div>

        {/* Distance Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-6 max-w-md mx-auto mb-10"
          data-testid="distance-calculator"
        >
          <h3 className="font-bold text-lg mb-4 text-center">Calculate Travel Fee</h3>
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Enter your suburb"
                  value={suburb}
                  onChange={handleInputChange}
                  onFocus={() => setShowSuggestions(true)}
                  className="bg-slate-800 border-slate-700 pl-10"
                  data-testid="suburb-input"
                />
                {/* Google Places Autocomplete suggestions */}
                {showSuggestions && predictions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden z-50 max-h-64 overflow-y-auto shadow-xl">
                    {predictions.map((prediction, i) => (
                      <button
                        key={i}
                        onClick={() => selectPrediction(prediction)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center gap-3 border-b border-slate-700 last:border-0"
                      >
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-sm">
                          <span className="font-medium">{prediction.main_text}</span>
                          {prediction.secondary_text && (
                            <span className="text-slate-400 ml-1 text-xs">{prediction.secondary_text}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchLoading && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg p-3 z-50">
                    <div className="flex items-center gap-2 text-slate-400">
                      <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  </div>
                )}
              </div>
              <Button 
                onClick={() => calculateFee()}
                className="btn-primary-glow text-slate-900 px-6"
                data-testid="calculate-fee-btn"
                disabled={loading || !suburb.trim()}
              >
                {loading ? "..." : "Check"}
              </Button>
            </div>
          </div>
          
          {loading && (
            <div className="mt-4 p-4 bg-slate-800 rounded-lg text-center">
              <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Calculating distance...</p>
            </div>
          )}
          
          {!loading && calculatedFee !== null && (
            <div className="mt-4 p-4 bg-slate-800 rounded-lg" data-testid="calculated-fee">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">
                  {calculatedFee.suburb} • {calculatedFee.distance}km from Thomastown
                  {calculatedFee.unknown && " (estimated)"}
                </p>
                {calculatedFee.fee === 0 ? (
                  <p className="text-green-400 font-bold text-lg">Free travel!</p>
                ) : (
                  <>
                    <p className="text-cyan-400 font-bold text-lg">
                      Travel fee: ${calculatedFee.fee.toFixed(2)}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      ({(calculatedFee.distance - FREE_RADIUS_KM).toFixed(1)}km × $0.40/km)
                    </p>
                  </>
                )}
                {calculatedFee.source === "google" && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Powered by Google Maps
                  </p>
                )}
              </div>
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            Free travel within {FREE_RADIUS_KM}km • ${FEE_PER_KM.toFixed(2)}/km beyond
          </p>
        </motion.div>

        {/* Area Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {suburbs.slice(0, 12).map((s, i) => {
            const fee = calculateTravelFee(s.distance);
            return (
              <div 
                key={i} 
                className={`service-area-card ${fee === 0 ? "free" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{s.name}</span>
                  {fee === 0 ? (
                    <span className="text-xs text-green-400">Free</span>
                  ) : (
                    <span className="text-xs text-slate-400">+${fee.toFixed(2)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Don't see your area? Use the calculator above or contact us!
        </p>
      </div>
    </section>
  );
};

export default ServiceAreasSection;
